import type {
  WorkbookDefinition,
  WorkbookDistinctValues,
  WorkbookRunComparison,
  WorkbookRunComparisonKind,
} from "@erp/shared-types";
import { stableSerialize } from "@erp/shared-utils";
import type { WorkbookPlan } from "./planTypes";
import { sheetKind } from "./sheetKinds";

/** One Worksheet's Result figures in one Run, as its Result records them. */
export interface ResultFigures {
  sheetId: string;
  fingerprint: string;
  rowCount: number;
  distinctValues?: readonly WorkbookDistinctValues[];
  totals?: readonly { column: string; total: number | null }[];
  /** The rows entering the filters: the staged rows Distinct values count. */
  filterCounts?: { inputRowCount: number };
}

/** What comparing a Worksheet reads of its settings: its name, Text columns and lookups' result columns. */
export interface ComparedSheet {
  sheetId: string;
  name: string;
  textColumns: readonly string[];
  /** Fields holding a lookup's result and #N/A on a miss. */
  missColumns: readonly string[];
}

/** The Worksheets a comparison reads, from the Definition a Run executed. */
export function comparedSheets(
  definition: WorkbookDefinition,
  plan: WorkbookPlan,
): ComparedSheet[] {
  return definition.sheets.map((sheet) => {
    const kind = sheetKind(sheet);
    const sheetPlan = plan.sheets.get(sheet.id);
    const lineage = sheetPlan ? kind.lineage(sheetPlan) : undefined;
    return {
      sheetId: sheet.id,
      name: sheet.name,
      textColumns: kind
        .resultColumns()
        .filter((column) => column.type === "text")
        .map((column) => column.name),
      missColumns: [
        ...new Set(
          (lineage?.lookups ?? []).flatMap((lookup) =>
            lookup.missColumn ? [lookup.missColumn] : [],
          ),
        ),
      ],
    };
  });
}

interface Tolerances {
  /** A row count or total this far off (a ratio either way) is a warning. */
  ratio: number;
  /** A miss rate this much higher (absolute) is a warning, when it at least doubled. */
  missRate: number;
  /** Value shares this far apart (total variation distance) are a warning. */
  categories: number;
}

/**
 * Run over run a Workbook's figures move a little every period; a sample,
 * scaled up, is only roughly the full data (a Source kept whole doesn't
 * shrink, strata oversample edge cases), so it is held to wider bounds.
 */
const TOLERANCES: Record<WorkbookRunComparisonKind, Tolerances> = {
  previous: { ratio: 1.25, missRate: 0.005, categories: 0.15 },
  sample: { ratio: 2, missRate: 0.1, categories: 0.2 },
};
/** Text columns with at most this many values are compared as categories. */
const MAX_CATEGORIES = 20;
const MAX_WARNINGS = 50;

function rowsOf(values: WorkbookDistinctValues): number {
  return (
    values.values.reduce((sum, item) => sum + item.count, 0) +
    values.emptyCount +
    values.errorCount
  );
}

/** Each value's share of the rows, Empty and Error cells as values of their own. */
function shares(values: WorkbookDistinctValues): Map<string, number> {
  const total = rowsOf(values);
  const counts: [string, number][] = [
    ...values.values.map((item): [string, number] => [
      `v:${item.value}`,
      item.count,
    ]),
    ["empty", values.emptyCount],
    ["error", values.errorCount],
  ];
  return new Map(
    counts.map(([value, count]) => [value, total ? count / total : 0]),
  );
}

/** Total variation distance between two value shares: half the summed differences. */
function distance(
  a: ReadonlyMap<string, number>,
  b: ReadonlyMap<string, number>,
): number {
  let sum = 0;
  for (const key of new Set([...a.keys(), ...b.keys()]))
    sum += Math.abs((a.get(key) ?? 0) - (b.get(key) ?? 0));
  return Math.min(1, sum / 2);
}

function farApart(baseline: number, current: number, ratio: number): boolean {
  if (baseline === 0 || current === 0) return baseline !== current;
  if (Math.sign(baseline) !== Math.sign(current)) return true;
  const change = current / baseline;
  return change > ratio || change < 1 / ratio;
}

function format(value: number): string {
  return value.toLocaleString("en-US", { maximumFractionDigits: 2 });
}

function percent(value: number): string {
  const rounded = Math.round(value * 1000) / 10;
  return `${rounded}%`;
}

function findColumn<T extends { column: string }>(
  items: readonly T[] | undefined,
  column: string,
): T | undefined {
  const lower = column.toLowerCase();
  return items?.find((item) => item.column.toLowerCase() === lower);
}

/** A lookup's share of rows with no match: its result Field's #N/A (and other error) cells. */
/**
 * A lookup's share of rows with no match: its result Field's #N/A (and other
 * error) cells over the staged rows. Distinct values leave out rare values
 * when truncated, so only a complete list, or the Filter counts, give the
 * row count; without either there is no rate.
 */
function missRate(figures: ResultFigures, column: string): number | null {
  const values = findColumn(figures.distinctValues, column);
  if (!values) return null;
  const rows =
    figures.filterCounts?.inputRowCount ??
    (values.truncated ? undefined : rowsOf(values));
  return rows ? Math.min(1, values.errorCount / rows) : null;
}

export interface RunComparisonInput {
  kind: WorkbookRunComparisonKind;
  baselineRunId: string;
  /** What the baseline's counts and totals are multiplied by: 1, or a Sampled baseline's inverted share of keys. */
  scale: number;
  sheets: readonly ComparedSheet[];
  baseline: readonly ResultFigures[];
  current: readonly ResultFigures[];
}

/**
 * Compares a Run's Results with a baseline Run's, per Worksheet both hold:
 * row counts, each lookup's miss rate, few-valued Text columns' value
 * shares, and Number totals, each with a relative bound (`TOLERANCES`), and
 * a warning per shift past one. The one comparison engine behind run-over-run
 * drift and a full Run against the sample before it.
 */
export function compareRuns(input: RunComparisonInput): WorkbookRunComparison {
  const bounds = TOLERANCES[input.kind];
  const scale = input.scale > 0 ? input.scale : 1;
  const against =
    input.kind === "sample" ? "the sample suggested" : "the previous run had";
  const warnings: string[] = [];
  const alerts: WorkbookRunComparison["alerts"] = [];
  const warn = (
    message: string,
    alert: WorkbookRunComparison["alerts"][number],
  ) => {
    if (warnings.length >= MAX_WARNINGS) return;
    warnings.push(message);
    alerts.push(alert);
  };
  const sheets = input.sheets.flatMap((sheet) => {
    const baseline = input.baseline.find(
      (item) => item.sheetId === sheet.sheetId,
    );
    const current = input.current.find(
      (item) => item.sheetId === sheet.sheetId,
    );
    if (!baseline || !current) return [];
    const { name } = sheet;
    const expectedRows = baseline.rowCount * scale;
    if (farApart(expectedRows, current.rowCount, bounds.ratio))
      warn(
        `'${name}' has ${format(current.rowCount)} rows; ${against} ${input.kind === "sample" ? "about " : ""}${format(expectedRows)}.`,
        {
          kind: "rows",
          sheetId: sheet.sheetId,
          column: null,
          baseline: expectedRows,
          current: current.rowCount,
        },
      );
    const lookups = sheet.missColumns.map((column) => {
      const before = missRate(baseline, column);
      const now = missRate(current, column);
      if (
        before !== null &&
        now !== null &&
        now - before >= bounds.missRate &&
        now >= before * 2
      )
        warn(
          `The lookup in '${column}' of '${name}' found no match for ${percent(now)} of rows; ${against} ${percent(before)}.`,
          {
            kind: "miss-rate",
            sheetId: sheet.sheetId,
            column,
            baseline: before,
            current: now,
          },
        );
      return { column, missRate: { baseline: before, current: now } };
    });
    const text = new Set(
      sheet.textColumns.map((column) => column.toLowerCase()),
    );
    const categories = (current.distinctValues ?? []).flatMap((values) => {
      if (
        !text.has(values.column.toLowerCase()) ||
        values.truncated ||
        values.values.length > MAX_CATEGORIES
      )
        return [];
      const before = findColumn(baseline.distinctValues, values.column);
      if (!before || before.truncated) return [];
      const gap = distance(shares(before), shares(values));
      if (gap > bounds.categories)
        warn(
          `'${values.column}' in '${name}' is spread over its values differently from ${input.kind === "sample" ? "the sample" : "the previous run"} (${Math.round(gap * 100)}% of rows moved).`,
          {
            kind: "categories",
            sheetId: sheet.sheetId,
            column: values.column,
            baseline: null,
            current: gap,
          },
        );
      return [{ column: values.column, distance: gap }];
    });
    const totals = (current.totals ?? []).map(({ column, total }) => {
      const before = findColumn(baseline.totals, column)?.total ?? null;
      const expected = before === null ? null : before * scale;
      if (
        expected !== null &&
        total !== null &&
        farApart(expected, total, bounds.ratio)
      )
        warn(
          `The total of '${column}' in '${name}' is ${format(total)}; ${against} ${input.kind === "sample" ? "about " : ""}${format(expected)}.`,
          {
            kind: "total",
            sheetId: sheet.sheetId,
            column,
            baseline: expected,
            current: total,
          },
        );
      return { column, baseline: expected, current: total };
    });
    return [
      {
        sheetId: sheet.sheetId,
        changed: baseline.fingerprint !== current.fingerprint,
        rows: { baseline: expectedRows, current: current.rowCount },
        lookups,
        categories,
        totals,
      },
    ];
  });
  return {
    kind: input.kind,
    baselineRunId: input.baselineRunId,
    scale,
    sheets,
    warnings,
    alerts,
  };
}

/** What choosing a comparison's baseline needs from a listed Run. */
export interface ComparedRun {
  id: string;
  status: string;
  mode?: string;
  /** A Sampled run's Sampling; compared by value. */
  sampling?: { sampling: unknown } | null;
}

/**
 * The Runs a completed Run is compared with, from `earlier` (older Runs,
 * newest first): the previous comparable Run — a full Run's previous full
 * Run, a Sampled run's previous Sampled run by the same Sampling — and, for
 * a full Run, the Sampled run before it.
 */
export function comparisonBaselines<Run extends ComparedRun>(
  run: ComparedRun,
  earlier: readonly Run[],
): { kind: WorkbookRunComparisonKind; baseline: Run }[] {
  const completed = earlier.filter((item) => item.status === "completed");
  const isSample = (item: ComparedRun) => item.mode === "sample";
  const sameSampling = (item: ComparedRun) =>
    stableSerialize(item.sampling?.sampling ?? null) ===
    stableSerialize(run.sampling?.sampling ?? null);
  const previous = completed.find((item) =>
    isSample(run) ? isSample(item) && sameSampling(item) : !isSample(item),
  );
  const sample = isSample(run) ? undefined : completed.find(isSample);
  return [
    ...(previous ? [{ kind: "previous" as const, baseline: previous }] : []),
    ...(sample ? [{ kind: "sample" as const, baseline: sample }] : []),
  ];
}
